
import { idToType, typeToServiceType } from '~/service/facilities/facility-types';
import {
  UnifiedPOI,
  VIEWPOINT_PADDING,
  getOptions,
  getPOI,
  getErrorPOI
} from './unified-poi';

const matchWith = str => test => {
  if (typeof test == 'string') return test == str;
  if (test instanceof RegExp)  return test.test(str);
  throw new Error(`Unknown test type ${test}`);
};

const codeRegx = /^((sub|via):)?([^:]*)(::(.*))?/;

export {
  VIEWPOINT_PADDING,
  UnifiedPOI,
  getOptions,
  getPOI,
  getErrorPOI
};

export const excludeErrorPOIs = ({ isError }) => !isError;

// toViewpoint :: (Position, Number) -> POIOptions
//    Position = { x:Number, y:Number, z:Number? }
export const toViewpoint = (position, radius) => typeof radius == 'number' ?
  Object.assign({ radius }, position) : position;

// getViewpoint :: (POIid, Mapviewer, Object) -> Viewpoint
//    POIid = String
//    Viewpoint = { x: Number, y: Number, radius: Number }
export const getViewpoint = (id, mapviewer, padding = VIEWPOINT_PADDING) => {
  const points = mapviewer.getFootprintPoints(id);
  const viewpoint = mapviewer.getViewpointFromPositions(
    Object.assign({ points }, padding)
  );
  return viewpoint || mapviewer.camera.position;
};

// splitCode :: String -> [String]
export const splitCode = (spaceCode = '') => spaceCode.split(/[,\s]+/g);

// decodeSpaceCode :: String -> Object
export function decodeSpaceCode (code = '') {
  const [
    /* full match */,
    /* sub: */,
    type = 'normal',
    spaceCode,
    /* ::subCode */,
    subCode
  ] = code.match(codeRegx);
  return {
    type,
    subCode,
    spaceCode
  };
}

export function splitAndDecode (code) {
  return splitCode(code).map(decodeSpaceCode);
}

// getClosest :: ([String], VGContext) -> Promise {Request, Data}
export function getClosest (destinations, vgContext) {
  const { mapviewer } = vgContext;
  const { imdHash } = vgContext.options;

  destinations = destinations.filter(x => !!mapviewer.getPOI(x));

  switch (destinations.length) {
  case 0: return Promise.reject(`No POIs found for ${destinations}`);
  case 1: return Promise.resolve(destinations[0]);
  }

  return mapviewer.computeRoute({
    src: imdHash,
    dst: destinations,
    destinationOrder: 'closest'
  })
    .then(({request, data}) => {
      const { error, destinationIndices } = data;
      if (error) {
        const [
          routingErr,
          badIndex] = error.match(/routable.dst ([^\s]+) not routable/) || [false];
        return Promise.reject(new Error(
          routingErr ? `POI '${destinations[badIndex]}' not routable` :
            error
        ));
      }
      const [closestIndex] = destinationIndices || [];
      // const closestDst = request.dst[closestIndex];
      // return getOptions(closestDst, mapviewer);
      return request.dst[closestIndex];
    });
}

// getClosestService :: (Number, VGContext) -> Promise POIOptions
export function getClosestService (serviceType, vgContext) {
  const { mapviewer, serviceMap } = vgContext;
  const service = serviceMap[serviceType];
  if (!service) return Promise.reject(new Error(`No service '${serviceType}' in map data`));

  return getClosest(service.pois, vgContext)
    .then(closestDst => {
      return getOptions(closestDst, mapviewer);
    });
}

// getLocalized :: (Mapviewer.ExtraData, String) -> Resources
export function getLocalized (extraData = {}, lang) {
  const { resources = {} } = extraData;

  if (!resources[lang]) {
    lang = 'default';
  }
  if (resources[lang]) {
    return resources[lang].localized.locale[lang];
  } else {
    return {};
  }
}

// toCategoriesLookup :: ([String|Regex], [CategoryTag]) -> Categories
//    Categories = { [CategoryId]: { name: String } }
//    CategoryId = String Number
export function toCategoriesLookup (include, categories) {
  return Object.entries(categories).reduce((acc, [k,v]) => {
    if (include.some(matchWith(v.name))) acc[k] = v;
    return acc;
  }, {});
}

// toCategoryFilters :: (Categories, [CategoryTag]) -> CategoryFilters
//    CategoryFilters = {
//      hasCategory: PlaceEntry -> Boolean
//      matchingCategories: PlaceExtra -> [CategoryTag]
//      }
//    PlaceEntry = [String, PlaceExtra]
//    PlaceExtra = { name: String, categories?: [CategoryId] }
export function toCategoryFilters (include, categories) {
  const categoryLookup = toCategoriesLookup(include, categories);
  return {
    hasCategory: ([, { categories }]) =>
      !!categories && categories.some(x => categoryLookup[x]),
    matchingCategories: ({ categories = [] }) =>
      categories.reduce((acc, x) => {
        const cat = categoryLookup[x];
        if (cat) acc.push(cat.name);
        return acc;
      }, [])
  };
}

// getPlacesByCategories :: ([CategoryTag], Mapviewer, String) -> { [String]: Place }
//    CategoryTag = String
export function getPlacesByCategories (include = [], mapviewer, lang) {
  const { places, categories = {} } = getLocalized(
    mapviewer.getExtraData(), lang
  );

  const { hasCategory } = toCategoryFilters(include, categories);

  return Object.entries(places)
    .filter(hasCategory)
    .reduce((acc, [k,v]) => (acc[k] = v, acc), {});
}

// groupPlacesByCategories :: ([CategoryTag], Mapviewer, String) -> CategoryMap
//    CategoryMap = { [CategoryTag]: { [String]: Place } }
export function groupPlacesByCategories (include = [], mapviewer, lang) {
  const { places, categories = {} } = getLocalized(
    mapviewer.getExtraData(), lang
  );

  const { matchingCategories } = toCategoryFilters(include, categories);

  return Object.entries(places)
    .reduce((acc, [id, placeExtra]) => {
      const placeCategories = matchingCategories(placeExtra);
      placeCategories.forEach(categoryTag => {
        acc[categoryTag] = Object.assign({}, acc[categoryTag], { [id]: placeExtra });
      });
      return acc;
    },
    {}
    );
}

// hideIMDs :: Mapviewer -> void  // SIDE EFFECTS
export function hideIMDs (mapviewer) {
  const places = getPlacesByCategories(['imd'], mapviewer);

  // Hide all IMD POIs
  Object.keys(places)
    .map(getPOI(mapviewer))
    .forEach(([poi]) => poi.hide());
}

export function collectServices (vgContext) {

  const { mapviewer } = vgContext;
  const { serviceMapping, floorMapping } = vgContext.options;

  // Service POIs are the places in a service category and, like the service
  // buttons, the POIs whose id names the service type ('2-toilet-01')
  const serviceIds = new Map();
  const addIds = (service, ids) => {
    if (!service) return;
    const set = serviceIds.get(service) || new Set();
    ids.forEach(id => set.add(id));
    serviceIds.set(service, set);
  };

  const serviceGroups = groupPlacesByCategories(Object.keys(serviceMapping), mapviewer);
  Object.entries(serviceGroups).forEach(([categoryTag, places]) =>
    addIds(serviceMapping[categoryTag], Object.keys(places))
  );

  Object.keys(mapviewer.getPOIs()).forEach(id => {
    const type = idToType(id);
    if (type) addIds(serviceMapping[typeToServiceType(type).id], [id]);
  });

  return Array.from(serviceIds).reduce((acc, [service, ids]) => {
    acc.serviceTypes.push(service);
    acc.serviceMap[service.id] = service;
    service.pois = [];
    service.floors = {};

    Array.from(ids)
      .map(getPOI(mapviewer))
      .map(([poi]) => poi.options())
      .filter(excludeErrorPOIs)
      .forEach(poi => {
        const vgFloorID = poi.floor;
        const { floor } = floorMapping[vgFloorID] || {};
        if (!floor) return console.warn(`Service POI '${poi.id}' on unknown floor '${vgFloorID}'`);

        // Service locations used for checking which floors those are available
        // Add max one per floor to fool Map#getClosestServiceOnFloor
        if (!service.floors[vgFloorID]) {
          service.floors[vgFloorID] = floor;
          acc.serviceLocations.push({
            id: poi.id,
            floorIndex: floor.order,
            serviceType: service.id
          });
        }
        service.pois.push(poi.id);
        acc.servicePOIs[poi.id] = service;
      });
    return acc;

  }, { servicePOIs: {}, serviceTypes: [], serviceMap: {}, serviceLocations: [] });
}

/* Helps finding issues in map data */
export function validateMapData (mapviewer, validCategories) {
  const { categories = {} } = getLocalized(
    mapviewer.getExtraData()
  );

  const unknownCategories = Object.values(categories)
    .filter(x => !validCategories.includes(x.name))
    .map(({ name }) => name);

  if (unknownCategories.length)
    console.warn(`Unknown VG map categories ${unknownCategories.join(', ')}`);

  const addIfFail = (issue, testFn, ids = []) => (id) => {
    if (id == undefined) return { issue, ids };
    if (testFn(id)) {
      ids.push(id);
    }
  };

  const pois = mapviewer.getPOIs();
  Object.keys(pois)
    .reduce((acc, id) => (acc.forEach(check => check(id)), acc),
      [
        addIfFail(
          'remove white space',
          id => id.startsWith(' ') || id.endsWith(' ')
        ),
        addIfFail(
          'must begin with [A-Za-z] if used as selector',
          id => /^[^a-z]/i.test(id)
        )
      ]
    )
    .map(x => x())
    .forEach(({ issue, ids }) => {
      if (ids.length) console.warn(`Bad POI id "${issue}"\n  ${ids.join(', ')}`);
    });

}
